import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  MenuItem,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import {
  addMonths,
  endOfMonth,
  format,
  isBefore,
  startOfDay,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../common/ConfirmDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SalovareDateCalendar from '../common/SalovareDateCalendar';
import SalovareSelectField from '../common/SalovareSelectField';
import { buildSelectFieldSx, fieldLabelSx } from '../common/formFieldStyles';
import { useToast } from '../../context/ToastContext';
import TeamMemberService from '../../services/teamMemberService';
import requestDeduplicator from '../../utils/requestDeduplicator';
import { formatCalendarDate, formatClockTime } from '../../utils/dateFormatUtils';
import { TIME_OPTIONS } from '../../utils/weeklyHoursUtils';
import { BRAND } from '../../theme/brandTokens';

const DEFAULT_FROM_TIME = '09:00';
const DEFAULT_TO_TIME = '19:00';
const LOAD_ERROR_KEY = 'teamMembers.specialOpenings.error.loading';

const toDateKey = (value) => {
  if (!value) return '';
  if (value instanceof Date) return format(value, 'yyyy-MM-dd');
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
};

const toTimeValue = (value) => (formatClockTime(value) === '—' ? '' : formatClockTime(value));

const buildLoadRange = () => {
  const today = new Date();
  return {
    from: format(startOfMonth(subMonths(today, 1)), 'yyyy-MM-dd'),
    to: format(endOfMonth(addMonths(today, 12)), 'yyyy-MM-dd'),
  };
};

export const SpecialOpeningDay = (props) => {
  const { selectedDates = [], existingDates = new Set(), day, outsideCurrentMonth, ...other } = props;
  const key = toDateKey(day);
  const selected = selectedDates.includes(key);
  const existing = existingDates.has(key);

  return (
    <PickersDay
      {...other}
      day={day}
      outsideCurrentMonth={outsideCurrentMonth}
      selected={selected}
      sx={{
        fontWeight: selected || existing ? 700 : 500,
        ...(existing && !selected ? {
          border: `2px solid ${BRAND.accent}`,
          color: BRAND.accentText,
        } : null),
      }}
    />
  );
};

const TimeSelect = ({ value, onChange, disabled, error, label, sx }) => (
  <SalovareSelectField
    label={label}
    value={value}
    onChange={onChange}
    disabled={disabled}
    error={error}
    sx={sx}
  >
    {TIME_OPTIONS.map((option) => (
      <MenuItem key={option.value} value={option.value}>{option.value}</MenuItem>
    ))}
  </SalovareSelectField>
);

const TeamMemberSpecialOpeningsPanel = ({ storeId, teamMemberId, canUpdate }) => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useToast();
  const dateRange = useMemo(() => buildLoadRange(), []);

  const [openings, setOpenings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadErrorKey, setLoadErrorKey] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [hoursByDate, setHoursByDate] = useState({});
  const [defaultFrom, setDefaultFrom] = useState(DEFAULT_FROM_TIME);
  const [defaultTo, setDefaultTo] = useState(DEFAULT_TO_TIME);
  const [formError, setFormError] = useState(null);
  const [openingToDelete, setOpeningToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const memberOpenings = useMemo(
    () => (openings || [])
      .filter((opening) => opening.teamMemberId === teamMemberId)
      .sort((left, right) => toDateKey(left.date).localeCompare(toDateKey(right.date))),
    [openings, teamMemberId],
  );

  const existingDateKeys = useMemo(
    () => new Set(memberOpenings.map((opening) => toDateKey(opening.date))),
    [memberOpenings],
  );

  const loadOpenings = useCallback(async () => {
    if (!storeId) return;
    setLoading(true);
    setLoadErrorKey(null);
    try {
      const specialOpenings = await requestDeduplicator.deduplicate(
        `special-openings-${storeId}-${dateRange.from}-${dateRange.to}`,
        () => TeamMemberService.getSpecialOpenings(storeId, dateRange),
      );
      setOpenings(Array.isArray(specialOpenings) ? specialOpenings : []);
    } catch {
      setLoadErrorKey(LOAD_ERROR_KEY);
    } finally {
      setLoading(false);
    }
  }, [dateRange, storeId]);

  useEffect(() => {
    loadOpenings();
  }, [loadOpenings]);

  const toggleDate = (day, selectionState) => {
    if (selectionState && selectionState !== 'finish') return;
    if (!day) return;
    if (isBefore(startOfDay(day), startOfDay(new Date()))) return;
    const key = toDateKey(day);
    setSelectedDates((prev) => {
      if (prev.includes(key)) {
        setHoursByDate((hours) => {
          const next = { ...hours };
          delete next[key];
          return next;
        });
        return prev.filter((item) => item !== key);
      }
      setHoursByDate((hours) => ({ ...hours, [key]: { from: defaultFrom, to: defaultTo } }));
      return [...prev, key].sort();
    });
  };

  const updateRowHours = (dateKey, field, value) => {
    setHoursByDate((prev) => ({
      ...prev,
      [dateKey]: { ...(prev[dateKey] || { from: defaultFrom, to: defaultTo }), [field]: value },
    }));
  };

  const removeSelectedDate = (dateKey) => {
    setSelectedDates((prev) => prev.filter((item) => item !== dateKey));
    setHoursByDate((prev) => {
      const next = { ...prev };
      delete next[dateKey];
      return next;
    });
  };

  const handleSave = async () => {
    setFormError(null);
    if (selectedDates.length === 0) {
      setFormError({ key: 'teamMembers.specialOpenings.validation.datesRequired' });
      return;
    }

    const days = selectedDates.map((date) => {
      const hours = hoursByDate[date] || { from: defaultFrom, to: defaultTo };
      return { date, from: hours.from, to: hours.to };
    });

    if (days.some((day) => !day.from || !day.to || day.from >= day.to)) {
      setFormError({ key: 'teamMembers.specialOpenings.validation.invalidPeriod' });
      return;
    }

    setSaving(true);
    try {
      await TeamMemberService.upsertSpecialOpenings(storeId, { teamMemberIds: [teamMemberId], days });
      showSuccess(t('teamMembers.specialOpenings.success.saved'));
      setSelectedDates([]);
      setHoursByDate({});
      await loadOpenings();
    } catch (error) {
      const message = error?.response?.data?.message || t('teamMembers.specialOpenings.error.save');
      showError(message);
      setFormError({ message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!openingToDelete) return;
    setDeleting(true);
    try {
      await TeamMemberService.deleteSpecialOpening(storeId, openingToDelete.id);
      showSuccess(t('teamMembers.specialOpenings.success.deleted'));
      setOpeningToDelete(null);
      await loadOpenings();
    } catch {
      showError(t('teamMembers.specialOpenings.error.delete'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      {loadErrorKey && (
        <Alert severity="error" sx={{ mb: 2 }}>{t(loadErrorKey)}</Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 360px) minmax(0, 1fr)' },
          gap: { xs: 3, md: 4 },
          alignItems: 'start',
        }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ ...fieldLabelSx }}>
            {t('teamMembers.specialOpenings.calendarLabel')}
          </Typography>
          <SalovareDateCalendar
            value={null}
            onChange={canUpdate ? toggleDate : undefined}
            disablePast
            slots={{ day: SpecialOpeningDay }}
            slotProps={{ day: { selectedDates, existingDates: existingDateKeys } }}
          />
          <Typography variant="caption" sx={{ color: BRAND.textMuted, display: 'block', mt: 1 }}>
            {t('teamMembers.specialOpenings.calendarHelp')}
          </Typography>
        </Box>

        <Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <TimeSelect
              label={t('teamMembers.specialOpenings.defaultFrom')}
              value={defaultFrom}
              onChange={(event) => setDefaultFrom(event.target.value)}
              disabled={!canUpdate}
            />
            <TimeSelect
              label={t('teamMembers.specialOpenings.defaultTo')}
              value={defaultTo}
              onChange={(event) => setDefaultTo(event.target.value)}
              disabled={!canUpdate}
            />
          </Box>

          {selectedDates.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('teamMembers.specialOpenings.emptySelection')}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {selectedDates.map((dateKey) => {
                const hours = hoursByDate[dateKey] || { from: defaultFrom, to: defaultTo };
                const invalid = hours.from >= hours.to;
                return (
                  <Box
                    key={dateKey}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', sm: 'minmax(110px, 140px) minmax(0, 1fr) auto' },
                      gap: 1,
                      alignItems: 'center',
                      p: 1.5,
                      border: `1px solid ${invalid ? '#f44336' : BRAND.line}`,
                      borderRadius: '8px',
                      bgcolor: BRAND.surface,
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {formatCalendarDate(dateKey)}
                    </Typography>
                    <IconButton
                      aria-label={`${t('common.delete')} ${formatCalendarDate(dateKey)}`}
                      onClick={() => removeSelectedDate(dateKey)}
                      disabled={!canUpdate}
                      sx={{
                        gridColumn: { xs: 2, sm: 3 },
                        gridRow: 1,
                        justifySelf: 'end',
                        width: 44,
                        height: 44,
                        color: BRAND.textMuted,
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                    <Box
                      sx={{
                        gridColumn: { xs: '1 / -1', sm: 2 },
                        gridRow: { xs: 2, sm: 1 },
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                        gap: 1,
                        minWidth: 0,
                      }}
                    >
                      <TimeSelect
                        value={hours.from}
                        onChange={(event) => updateRowHours(dateKey, 'from', event.target.value)}
                        disabled={!canUpdate}
                        error={invalid}
                        sx={{ '& .MuiInputBase-root': buildSelectFieldSx(invalid) }}
                      />
                      <TimeSelect
                        value={hours.to}
                        onChange={(event) => updateRowHours(dateKey, 'to', event.target.value)}
                        disabled={!canUpdate}
                        error={invalid}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

          {formError && (
            <Alert severity="error" sx={{ mt: 2 }}>{formError.message || t(formError.key)}</Alert>
          )}

          {canUpdate && (
            <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' }, mt: 3 }}>
              <PrimaryActionButton
                onClick={handleSave}
                disabled={saving}
                sx={{ width: { xs: '100%', sm: 'auto' }, minHeight: 48 }}
              >
                {saving ? t('teamMembers.specialOpenings.saving') : t('teamMembers.specialOpenings.save')}
              </PrimaryActionButton>
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ mt: 5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          {t('teamMembers.specialOpenings.existingTitle')}
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: BRAND.accent }} />
          </Box>
        ) : memberOpenings.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('teamMembers.specialOpenings.existingEmpty')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {memberOpenings.map((opening) => (
              <Box
                key={opening.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) auto auto',
                  gap: 2,
                  alignItems: 'center',
                  p: 1.5,
                  border: `1px solid ${BRAND.line}`,
                  borderRadius: '8px',
                  bgcolor: BRAND.surface,
                }}
              >
                <Typography sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {formatCalendarDate(opening.date)}
                </Typography>
                <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', color: BRAND.textMuted, whiteSpace: 'nowrap' }}>
                  {toTimeValue(opening.from)} – {toTimeValue(opening.to)}
                </Typography>
                {canUpdate ? (
                  <IconButton
                    aria-label={`${t('common.delete')} ${formatCalendarDate(opening.date)}`}
                    onClick={() => setOpeningToDelete(opening)}
                    sx={{ width: 44, height: 44, color: BRAND.textMuted }}
                  >
                    <DeleteIcon />
                  </IconButton>
                ) : <span />}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <ConfirmDialog
        open={Boolean(openingToDelete)}
        onClose={() => setOpeningToDelete(null)}
        onConfirm={handleDelete}
        title={t('teamMembers.specialOpenings.confirmDeleteTitle')}
        message={t('teamMembers.specialOpenings.confirmDeleteMessage')}
        confirmText={t('common.delete')}
        loading={deleting}
        closeOnConfirm={false}
      />
    </Box>
  );
};

export default TeamMemberSpecialOpeningsPanel;
